/**
 * The rate-card form (POST / PATCH /well-testing/rates).
 *
 * Currency is NEVER sent — a rate is in the company's currency, stamped by the
 * server (`currency_not_settable`); it is shown read-only. The fields that
 * apply to one kind only are shown — and sent — for that kind only:
 *
 *   crew / night / standby   a crew role            (`crew_role_not_people`)
 *   package                  the package            (`package_not_package_kind`)
 *   unit                     a unit OR a type        (`equipment_not_unit_kind`, `unit_and_type`)
 *   third_party              NO RATE — `pricing` at cost or a markup %; the
 *                            vendor's cost is entered on each ticket line
 *                            (`third_party_rate`, `markup_not_markup`)
 *   per_volume (a unit)      the volume unit        (`volume_not_per_volume`)
 */
import React, { useEffect, useState } from 'react';
import { blankToNull, toInput } from '../../../lib/well-testing';
import type { WtRate, WtRateWrite, WtTicketCatalog } from '../../../types/wt-tickets';
import { WT_DATE_INPUT, WT_INPUT, WT_PRIMARY, WT_SECONDARY, WtField, WtFormError } from '../WtBits';
import { PfFormGrid as WtFormGrid } from '../../pipeline-facility/digs/DigBits';

export interface WtRateFormOptions {
  customers: { id: string; name: string }[];
  equipment: { id: string; name: string; unitNumber?: string | null }[];
  equipmentTypes: { id: string; name: string }[];
  packages: { id: string; name: string }[];
}

/** The kinds a crew role prices (mirrors the API's WT_PEOPLE_KINDS). */
export const WT_PEOPLE_KINDS = ['crew', 'night', 'standby'];

export function WtRateForm({
  rate,
  catalog,
  options,
  currency,
  today,
  defaultCustomerId,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  rate?: WtRate | null;
  catalog: WtTicketCatalog | null;
  options: WtRateFormOptions;
  currency: string | null;
  today: string | null;
  defaultCustomerId?: string;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: WtRateWrite) => void;
}) {
  const [code, setCode] = useState(rate?.code ?? '');
  const [name, setName] = useState(rate?.name ?? '');
  const [kind, setKind] = useState(rate?.kind ?? '');
  const [unit, setUnit] = useState(rate?.unit ?? '');
  const [amount, setAmount] = useState(toInput(rate?.rate));
  const [pricing, setPricing] = useState(rate?.pricing ?? '');
  const [markup, setMarkup] = useState(toInput(rate?.markupPct));
  const [volumeUnit, setVolumeUnit] = useState(rate?.volumeUnit ?? '');
  const [customerId, setCustomerId] = useState(rate ? rate.customerId ?? '' : defaultCustomerId ?? '');
  const [crewRole, setCrewRole] = useState(rate?.crewRole ?? '');
  const [packageId, setPackageId] = useState(rate?.packageId ?? '');
  const [equipmentId, setEquipmentId] = useState(rate?.equipmentId ?? '');
  const [equipmentTypeId, setEquipmentTypeId] = useState(rate?.equipmentTypeId ?? '');
  const [from, setFrom] = useState(rate?.effectiveFrom ?? today ?? '');
  const [to, setTo] = useState(rate?.effectiveTo ?? '');
  const [description, setDescription] = useState(rate?.description ?? '');
  const [notes, setNotes] = useState(rate?.notes ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  // The tenant's day arrives with the rates query's meta, usually after the
  // first render; a new rate starts on it unless the office has picked a day.
  useEffect(() => {
    if (!rate && today) setFrom((f) => f || today);
  }, [rate, today]);

  const thirdParty = kind === 'third_party';
  const people = WT_PEOPLE_KINDS.includes(kind);

  const submit = () => {
    setProblem(null);
    if (!code.trim()) return setProblem('Enter a short code for the rate.');
    if (!name.trim()) return setProblem('Enter the rate’s name.');
    if (!kind || !unit) return setProblem('Pick what kind of charge it is and its unit.');
    if (thirdParty) {
      if (pricing !== 'at_cost' && pricing !== 'markup_pct') return setProblem('Pick how the vendor’s cost is billed: at cost, or cost plus a markup %.');
      if (pricing === 'markup_pct' && (!markup.trim() || !Number.isFinite(Number(markup)) || Number(markup) < 0))
        return setProblem('Enter the markup as a percentage of 0 or more.');
    } else if (!amount.trim() || !Number.isFinite(Number(amount)) || Number(amount) < 0) {
      return setProblem('Enter the rate as a number of 0 or more.');
    }
    if (unit === 'per_volume' && !volumeUnit.trim()) return setProblem('Enter the volume unit the rate is counted in (m³, bbl, …).');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) return setProblem('Enter the day the rate starts.');
    if (to && to < from) return setProblem('The end day can’t be before the start day.');
    if (equipmentId && equipmentTypeId) return setProblem('A rate prices one unit or one equipment type, not both. Pick one.');
    const body: WtRateWrite = {
      code: code.trim(),
      name: name.trim(),
      kind,
      unit,
      effective_from: from,
      effective_to: to || null,
      customer_id: customerId || null,
      description: blankToNull(description),
      notes: blankToNull(notes),
    };
    // A third-party rate carries no rate of its own (`third_party_rate`); it is never sent.
    if (thirdParty) {
      body.pricing = pricing;
      if (pricing === 'markup_pct') body.markup_pct = markup.trim();
    } else {
      body.rate = amount.trim();
    }
    if (unit === 'per_volume') body.volume_unit = volumeUnit.trim();
    if (people) body.crew_role = crewRole || null;
    if (kind === 'package') body.package_id = packageId || null;
    if (kind === 'unit') {
      body.equipment_id = equipmentId || null;
      body.equipment_type_id = equipmentTypeId || null;
    }
    onSubmit(body);
  };

  return (
    <div data-testid="wt-rate-form" className="space-y-4 px-4 py-4">
      <WtFormError message={problem ?? error} />
      <WtFormGrid>
        <WtField label="Code *" hint="Short, e.g. CREW-OP. Unique per customer card and period.">
          <input className={WT_INPUT} maxLength={30} value={code} onChange={(e) => setCode(e.target.value)} />
        </WtField>
        <WtField label="Name *">
          <input className={WT_INPUT} maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
        </WtField>
        <WtField label="Card" hint="The default card applies to every customer without a rate of their own.">
          <select className={WT_INPUT} aria-label="Card" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Default card (every customer)</option>
            {options.customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </WtField>
        <WtField label="Kind *">
          <select className={WT_INPUT} aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">Pick one</option>
            {(catalog?.priceKinds ?? []).map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </WtField>
        <WtField label="Unit *">
          <select className={WT_INPUT} aria-label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
            <option value="">Pick one</option>
            {(catalog?.priceUnits ?? []).map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
        </WtField>
        {thirdParty ? (
          <>
            <WtField label="Pricing *" hint="A third-party rate has no rate of its own: the vendor’s cost is entered on each ticket line.">
              <select className={WT_INPUT} aria-label="Pricing" value={pricing} onChange={(e) => setPricing(e.target.value)}>
                <option value="">Pick one</option>
                {(catalog?.thirdPartyPricing ?? []).map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </WtField>
            {pricing === 'markup_pct' && (
              <WtField label="Markup % *">
                <input className={WT_INPUT} inputMode="decimal" aria-label="Markup %" value={markup} onChange={(e) => setMarkup(e.target.value)} />
              </WtField>
            )}
          </>
        ) : (
          <WtField label="Rate *">
            <input className={WT_INPUT} inputMode="decimal" aria-label="Rate" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </WtField>
        )}
        <WtField label="Currency" hint="The company’s currency, set in Settings. Not set per rate.">
          <p data-testid="wt-rate-currency" className="flex min-h-11 items-center font-mono text-sm text-white">
            {currency ?? '—'}
          </p>
        </WtField>
        {unit === 'per_volume' && (
          <WtField label="Volume unit *" hint="What the rate is counted in, e.g. m³ or bbl.">
            <input className={WT_INPUT} maxLength={20} aria-label="Volume unit" value={volumeUnit} onChange={(e) => setVolumeUnit(e.target.value)} />
          </WtField>
        )}
        {people && (
          <WtField label="Crew role" hint="Optional. Prices the shifts of hands logged in this role.">
            <select className={WT_INPUT} aria-label="Crew role" value={crewRole} onChange={(e) => setCrewRole(e.target.value)}>
              <option value="">Any role</option>
              {(catalog?.crewRoles ?? []).map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </WtField>
        )}
        {kind === 'package' && (
          <WtField label="Package" hint="Optional. Blank prices any package.">
            <select className={WT_INPUT} aria-label="Package" value={packageId} onChange={(e) => setPackageId(e.target.value)}>
              <option value="">Any package</option>
              {options.packages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </WtField>
        )}
        {kind === 'unit' && (
          <>
            <WtField label="Equipment unit" hint="Optional. One unit, or one type — not both.">
              <select className={WT_INPUT} aria-label="Equipment unit" value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)}>
                <option value="">Any unit</option>
                {options.equipment.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.unitNumber ? `${q.unitNumber} · ${q.name}` : q.name}
                  </option>
                ))}
              </select>
            </WtField>
            <WtField label="Equipment type">
              <select className={WT_INPUT} aria-label="Equipment type" value={equipmentTypeId} onChange={(e) => setEquipmentTypeId(e.target.value)}>
                <option value="">Any type</option>
                {options.equipmentTypes.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.name}
                  </option>
                ))}
              </select>
            </WtField>
          </>
        )}
        <WtField label="Starts *">
          <input className={WT_DATE_INPUT} type="date" aria-label="Starts" value={from} onChange={(e) => setFrom(e.target.value)} />
        </WtField>
        <WtField label="Ends" hint="Blank = no end day.">
          <input className={WT_DATE_INPUT} type="date" aria-label="Ends" value={to} onChange={(e) => setTo(e.target.value)} />
        </WtField>
      </WtFormGrid>
      <WtField label="Description" hint="Optional.">
        <input className={WT_INPUT} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} />
      </WtField>
      <WtField label="Notes">
        <textarea className={`${WT_INPUT} h-20 py-2`} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </WtField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WT_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="button" className={WT_PRIMARY} onClick={submit} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
}
